import React, {PureComponent} from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import {InjectedIntl} from "react-intl"

import {FormattedMessage, injectIntl} from "@irati/utils/lang"
import Validation from "@irati/utils/validation"
import Form from "@irati/ui/src/Form"
import {Body} from "@irati/ui/src/Typography"
import Input from "@irati/ui/src/FormItem/Input"
import {Radio} from "@irati/ui/src/FormItem"
import DOBSelect from "@irati/ui/src/DOBSelect"
import authentication from "@irati/auth"
import {create} from "@irati/medical/services/medical"
import {MedicalDCAUser} from "@irati/medical/types"

import ContinueBackButtons from "../ContinueBackButtons"
import {SET_EMAIL} from "../../containers/Home/GetStarted"

const Content = styled.div`
  max-width: 360px;
  margin: 0 auto;
`

const Description = styled(Body)`
  max-width: 415px;
  margin: 0 auto 20px;
`

interface Props extends InjectedIntl {
  email: string
  onUpdateStep: (step: string) => void
  onCreateMedicalUser: (user: MedicalDCAUser) => void
}

interface Account {
  dob: string
  phone: string
  gender: string
}

interface State {
  account: Account
  load?: Promise<MedicalDCAUser> | null
}

class ProfileInformationStep extends PureComponent<Props, State> {
  static contextTypes = {
    appPath: PropTypes.string.isRequired
  }

  constructor(props: Props) {
    super(props)
    const {dob, phone, gender} = authentication.user()
    this.state = {
      load: null,
      account: {dob, phone, gender}
    }
  }

  buildGenders = () => {
    const {formatMessage} = this.props
    return [
      {label: formatMessage({id: "medical.details.genders.options.female"}), value: "F"},
      {label: formatMessage({id: "medical.details.genders.options.male"}), value: "M"},
      {label: formatMessage({id: "medical.details.genders.options.ratherNotSay"}), value: "X"}
    ]
  }
  proNounValidation = () => {
    return Validation.string().required(this.props.formatMessage({id: "platform.defaultValidation.radioRequired"}))
  }

  mobileValidation = () => {
    const {formatMessage} = this.props
    /* eslint-disable */
    return Validation.string(formatMessage({id: "platform.defaultValidation.required"}))
      .min(7, formatMessage({id: "account.profile.fields.phone.validation.min"}))
      .max(15, formatMessage({id: "account.profile.fields.phone.validation.max"}))
      .matches(
        /^[+]?([\d]{0,3})?[\(\.\-\s]?(([\d]{1,3})[\)\.\-\s]*)?(([\d]{3,5})[\.\-\s]?([\d]{4})|([\d]{2}[\.\-\s]?){4})$/,
        formatMessage({id: "account.profile.fields.phone.validation.matches"})
      )
    /* eslint-enable */
  }

  handleUpdatePhone = (phone: string) => {
    this.setState((state) => ({account: {...state.account, phone}}))
  }

  handleUpdateDOB = (dob: string) => {
    this.setState((state) => ({account: {...state.account, dob}}))
  }

  handleUpdateGender = (gender: string) => {
    this.setState((state) => ({account: {...state.account, gender}}))
  }

  handleCreateMedicalAccount = () => {
    const {phone, gender, dob} = this.state.account
    const {onCreateMedicalUser, email} = this.props

    const data = {
      email,
      phoneNumber: phone,
      gender,
      dob
    }
    const load = create(data).then((user: MedicalDCAUser) => {
      onCreateMedicalUser(user)
    })
    this.setState({load})
  }

  render() {
    const {formatMessage, onUpdateStep} = this.props
    const {
      account: {phone, dob, gender},
      load
    } = this.state

    return (
      <Form>
        <Body large maxWidth="100%" marginBottom={1} textAlign="center">
          <FormattedMessage id="medical.details.para" />
        </Body>
        <Description small weight="light">
          <FormattedMessage id="medical.details.desc" />
        </Description>
        <Content>
          <DOBSelect
            isMinimumAge18
            dayMonthYearRequired
            label={formatMessage({id: "medical.details.dob.label"})}
            value={dob}
            onChange={this.handleUpdateDOB}
          />
          <Radio
            name="preferredPronoun"
            label={formatMessage({id: "medical.details.gender.label"})}
            value={gender}
            options={this.buildGenders()}
            onChange={this.handleUpdateGender}
            validation={this.proNounValidation()}
          />
          <Input
            label={formatMessage({id: "medical.details.phone.label"})}
            tooltip={formatMessage({id: "medical.details.phone.tooltip"})}
            tooltipPosition="top"
            customTooltip
            placeholder={formatMessage({id: "medical.details.phone.placeholder"})}
            value={phone}
            name="phone"
            onChange={this.handleUpdatePhone}
            validation={this.mobileValidation()}
          />
          <ContinueBackButtons load={load} onBack={() => onUpdateStep(SET_EMAIL)} onContinue={this.handleCreateMedicalAccount} />
        </Content>
      </Form>
    )
  }
}

export default injectIntl(ProfileInformationStep)
